import React, { useState } from 'react';
import { ChevronRight, Factory, Zap, TrendingUp, Eye, Calculator, Workflow, ShoppingCart, FileText, Users, BarChart3, Clock, CheckCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const PortfolioWork = () => {
    const [activeProject, setActiveProject] = useState(0);

    // Updated projects array with consistent coloring
    const projects = [
        {
            id: 1,
            title: "Dyeing and Printing Mills",
            subtitle: "Inward to Delivery System",
            category: "Manufacturing",
            icon: Factory,
            // Base color scheme
            color: "from-blue-600 to-blue-800",
            accentColor: "blue",
            description: "Our Inward to Delivery system is a comprehensive solution built specifically for textile dyeing and printing units. It provides complete visibility across every stage of production—from raw material entry to final dispatch",
            features: [
                { icon: Eye, text: "End-to-End Tracking", desc: "Monitor goods from inward receipt to delivery with real-time accuracy" },
                { icon: Calculator, text: "Costing Management", desc: "Calculate and control costs at each stage to improve profitability" },
                { icon: Workflow, text: "Integrated Workflow", desc: "Seamlessly connect departments for smooth information flow" }
            ],
            benefits: "Streamline operations and provide precise data insights, reduce waste, improve efficiency, and maintain competitive edge in the fast-paced textile industry",
            stats: [
                { label: "Process Stages", value: "15+" },
                { label: "Cost Reduction", value: "30%" },
                { label: "Efficiency Boost", value: "45%" }
            ]
        },
        {
            id: 2,
            title: "Looms Management",
            subtitle: "From Old Flatbed to Modern Rapier & Jacquards",
            category: "Weaving",
            icon: Zap,
            // Changed to blue color scheme
            color: "from-blue-600 to-blue-800",
            accentColor: "blue",
            description: "From Fibre to Fabric, our loom tracking solution empowers manufacturers with detailed insights at every step of the weaving process",
            features: [
                { icon: ShoppingCart, text: "Sales Order Monitoring", desc: "Keep track of every order from booking to completion" },
                { icon: FileText, text: "Program Card Tracking", desc: "Digitally manage loom program cards for error-free production" },
                { icon: CheckCircle, text: "Dispatch Visibility", desc: "Ensure timely and accurate delivery with live status updates" }
            ],
            benefits: "Support loom owners by enhancing decision-making, optimizing resources, and boosting performance. Minimize downtime, reduce waste, and maximize output",
            stats: [
                { label: "Loom Types", value: "All" },
                { label: "Downtime Reduction", value: "40%" },
                { label: "Output Increase", value: "35%" }
            ]
        },
        {
            id: 3,
            title: "Trading Management",
            subtitle: "Complete Business Solution",
            category: "Commerce",
            icon: TrendingUp,
            // Changed to blue color scheme
            color: "from-blue-600 to-blue-800",
            accentColor: "blue",
            description: "Our trading management solution is built for textile traders who want to manage operations efficiently on a single platform",
            features: [
                { icon: BarChart3, text: "Accounts & Finance", desc: "Gain full control over financial records with simplified accounting tools" },
                { icon: Clock, text: "Inventory Management", desc: "Track stock movement in real time for accurate availability updates" },
                { icon: Users, text: "Payroll Integration", desc: "Manage employee salaries, attendance, and compliance with ease" }
            ],
            benefits: "All-in-one platform that brings together accounting, stock control, and HR management, simplifying workflows, saving time, and increasing profitability",
            stats: [
                { label: "Modules", value: "3 in 1" },
                { label: "Time Saved", value: "50%" },
                { label: "Profit Increase", value: "25%" }
            ]
        }
    ];

    return (
        <div className="bg-gray-50 py-20">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center px-4 py-2 rounded-full bg-blue-100 text-blue-800 text-sm font-medium mb-4">
                        <span className="w-2 h-2 bg-blue-600 rounded-full mr-2"></span>
                        Our Work
                    </div>
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                        Portfolio <span className="text-[#1C74BB]">Solutions</span>
                    </h2>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                        Comprehensive software solutions designed specifically for the textile industry,
                        from manufacturing to trading operations
                    </p>
                </div>

                {/* Project Navigation */}
                <div className="flex flex-wrap justify-center mb-12 space-x-4">
                    {projects.map((project, index) => {
                        const IconComponent = project.icon;
                        
                        // Simplified active styles since they are all the same now
                        const activeStyles = 'bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-lg shadow-blue-300/50';

                        return (
                            <button
                                key={project.id}
                                onClick={() => setActiveProject(index)}
                                className={`flex items-center px-6 py-3 rounded-xl font-medium transition-all duration-300 mb-4 transform ${
                                    activeProject === index
                                        ? `${activeStyles} scale-105 border-2 border-white/20`
                                        : 'bg-white text-gray-700 hover:bg-gray-100 hover:scale-102 shadow-md border-2 border-transparent'
                                }`}
                            >
                                <IconComponent className="w-5 h-5 mr-2" />
                                {project.category}
                            </button>
                        );
                    })}
                </div>

                {/* Active Project Display */}
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
                    <div className={`bg-gradient-to-r ${projects[activeProject].color} p-8 text-white`}>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <div className="p-4 bg-white/20 rounded-2xl">
                                    {React.createElement(projects[activeProject].icon, { className: "w-8 h-8" })}
                                </div>
                                <div>
                                    <div className="text-sm opacity-90 mb-1">{projects[activeProject].category}</div>
                                    <h3 className="text-3xl font-bold">{projects[activeProject].title}</h3>
                                    <p className="text-lg opacity-90 mt-1">{projects[activeProject].subtitle}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="p-8">
                        <div className="grid lg:grid-cols-2 gap-12">
                            {/* Project Description */}
                            <div>
                                {/* Project Illustration/Mockup */}
                                <div className="mb-8 relative">
                                    <div className={`bg-gradient-to-br ${projects[activeProject].color} rounded-2xl p-6 relative overflow-hidden`}>
                                        {/* Mock Dashboard/Interface */}
                                        <div className="bg-white/95 rounded-xl p-4 backdrop-blur-sm">
                                            <div className="flex items-center justify-between mb-4">
                                                <div className="flex space-x-2">
                                                    <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                                                    <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                                                    <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                                                </div>
                                                <div className="text-xs text-gray-500 font-mono">
                                                    {projects[activeProject].title} Dashboard
                                                </div>
                                            </div>
                                            
                                            {/* Dashboard Content Based on Project */}
                                            {activeProject === 0 && (
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                        <span className="text-sm font-medium text-gray-700">Raw Material In</span>
                                                        <span className={`text-sm text-${projects[activeProject].accentColor}-600 font-semibold`}>1,250 kg</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                                        <span className="text-sm font-medium text-gray-700">In Process</span>
                                                        <span className={`text-sm text-${projects[activeProject].accentColor}-600 font-semibold`}>847 kg</span>
                                                    </div>
                                                    <div className="flex justify-between items-center py-2">
                                                        <span className="text-sm font-medium text-gray-700">Ready for Dispatch</span>
                                                        <span className={`text-sm text-${projects[activeProject].accentColor}-600 font-semibold`}>403 kg</span>
                                                    </div>
                                                    <div className="mt-4 h-2 bg-gray-200 rounded-full overflow-hidden">
                                                        <div className={`h-full bg-${projects[activeProject].accentColor}-500 w-3/4 transition-all duration-1000`}></div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {activeProject === 1 && (
                                                <div className="space-y-3">
                                                    <div className="grid grid-cols-3 gap-2 text-center">
                                                        <div className="bg-blue-100 p-2 rounded">
                                                            <div className="text-lg font-bold text-blue-600">12</div>
                                                            <div className="text-xs text-gray-600">Running</div>
                                                        </div>
                                                        <div className="bg-yellow-100 p-2 rounded">
                                                            <div className="text-lg font-bold text-yellow-600">3</div>
                                                            <div className="text-xs text-gray-600">Setup</div>
                                                        </div>
                                                        <div className="bg-red-100 p-2 rounded">
                                                            <div className="text-lg font-bold text-red-600">1</div>
                                                            <div className="text-xs text-gray-600">Stopped</div>
                                                        </div>
                                                    </div>
                                                    <div className="text-center py-2">
                                                        <div className="text-sm font-medium text-gray-700">Today's Production</div>
                                                        <div className={`text-2xl font-bold text-${projects[activeProject].accentColor}-600`}>2,847m</div>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            {activeProject === 2 && (
                                                <div className="space-y-3">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm font-medium text-gray-700">Total Revenue</span>
                                                        <span className={`text-lg font-bold text-${projects[activeProject].accentColor}-600`}>₹12.5L</span>
                                                    </div>
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="text-center">
                                                            <div className="text-sm text-gray-600">Stock Items</div>
                                                            <div className={`text-xl font-bold text-${projects[activeProject].accentColor}-600`}>1,247</div>
                                                        </div>
                                                        <div className="text-center">
                                                            <div className="text-sm text-gray-600">Employees</div>
                                                            <div className={`text-xl font-bold text-${projects[activeProject].accentColor}-600`}>45</div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Floating Elements */}
                                        <div className="absolute -top-2 -right-2 w-8 h-8 bg-white/20 rounded-full animate-bounce"></div>
                                        <div className="absolute -bottom-2 -left-2 w-6 h-6 bg-white/10 rounded-full animate-pulse"></div>
                                    </div>
                                </div>

                                <h4 className="text-xl font-semibold text-gray-900 mb-4">Project Overview</h4>
                                <p className="text-gray-600 text-lg leading-relaxed mb-8">
                                    {projects[activeProject].description}
                                </p>

                                {/* Key Features */}
                                <h4 className="text-xl font-semibold text-gray-900 mb-6">Key Features</h4>
                                <div className="space-y-6">
                                    {projects[activeProject].features.map((feature, index) => {
                                        const FeatureIcon = feature.icon;
                                        return (
                                            <div key={index} className="flex items-start space-x-4">
                                                <div className={`p-3 bg-${projects[activeProject].accentColor}-100 rounded-xl`}>
                                                    <FeatureIcon className={`w-6 h-6 text-${projects[activeProject].accentColor}-600`} />
                                                </div>
                                                <div className="flex-1">
                                                    <h5 className="font-semibold text-gray-900 mb-1">{feature.text}</h5>
                                                    <p className="text-gray-600">{feature.desc}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Benefits & Stats */}
                            <div>
                                <h4 className="text-xl font-semibold text-gray-900 mb-4">Business Impact</h4>
                                <div className={`bg-gradient-to-r ${projects[activeProject].color} rounded-2xl p-6 text-white mb-8`}>
                                    <p className="text-lg leading-relaxed">
                                        {projects[activeProject].benefits}
                                    </p>
                                </div>

                                {/* Stats */}
                                <h4 className="text-xl font-semibold text-gray-900 mb-6">Key Metrics</h4>
                                <div className="grid grid-cols-3 gap-4 mb-8">
                                    {projects[activeProject].stats.map((stat, index) => (
                                        <div key={index} className="text-center p-4 bg-gray-50 rounded-xl">
                                            <div className={`text-2xl font-bold text-${projects[activeProject].accentColor}-600 mb-1`}>
                                                {stat.value}
                                            </div>
                                            <div className="text-sm text-gray-600">{stat.label}</div>
                                        </div>
                                    ))}
                                </div>

                                {/* CTA Button */}
                                <Link to={'/Contact'} className={`w-full bg-gradient-to-r ${projects[activeProject].color} text-white py-4 px-8 rounded-xl font-semibold text-lg hover:shadow-lg transition-all duration-300 flex items-center justify-center group`}>
                                    Learn More About This Solution
                                    <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Bottom CTA Section */}
               
            </div>
        </div>
    );
};

export default PortfolioWork;